// src/lib/utils/format/text.ts
// AUTO-SPLIT from lib/utils/formatters.ts (A10/A2). New code: import from ~/lib/utils/format.
import { CapitalizationStrategy } from './types'
import type { TitleCaseOptions, FormattingOptions } from './types'
import { DEFAULT_SPECIAL_CASES } from './data/special-cases'
import { DEFAULT_HYPHENATED_WORDS, DEFAULT_MINOR_WORDS, DEFAULT_WORD_DELIMITERS } from './data/hyphenated-words'

/**
 * Applies post-processing to formatted text with comprehensive options.
 * Handles trimming, space normalization, length limits, and truncation.
 * 
 * @param {string} text - The text to process
 * @param {object} options - Processing options
 * @returns {string} Processed text
 * @internal
 */
export function postProcessText(
  text: string, 
  options: {
    readonly trimWhitespace?: boolean
    readonly removeExtraSpaces?: boolean
    readonly maxLength?: number
    readonly truncateEllipsis?: string
  } = {}
): string {
  const {
    trimWhitespace = true,
    removeExtraSpaces = true,
    maxLength,
    truncateEllipsis = '...'
  } = options
  
  let result = text
  
  if (removeExtraSpaces) {
    result = result.replace(/\s+/g, ' ')
  }
  
  if (trimWhitespace) {
    result = result.trim()
  }
  
  if (maxLength && result.length > maxLength) {
    const truncateLength = Math.max(0, maxLength - truncateEllipsis.length)
    result = result.slice(0, truncateLength) + truncateEllipsis
  }
  
  return result
}


/**
 * Format a recommendation text with proper highlighting and formatting.
 * Automatically highlights metrics, currency, and percentages for better readability.
 * 
 * @param {string} text - The recommendation text to format
 * @returns {string} Formatted recommendation text with proper styling
 * 
 * @example
 * ```typescript
 * formatRecommendationText("Your MER is 4.0 which is excellent.")
 * // Returns text with highlighted "4.0" 
 * 
 * formatRecommendationText("Increase budget by $500 for 15% improvement")
 * // Returns text with highlighted "$500" and "15%"
 * ```
 */
export function formatRecommendationText(text: string): string {
  if (!text) return ''
  
  // Highlight metrics and percentages
  return text.replace(/\b(\d+(\.\d+)?%|\$\d+(,\d+)*(\.\d+)?|\d+(\.\d+)? ?x)\b/g, 
    (match) => `<span class="font-medium">${match}</span>`)
}

/**
 * Truncate text to a specified length with ellipsis.
 * 
 * @param {string} text - The text to truncate
 * @param {number} [length=100] - Maximum length before truncation
 * @param {string} [ellipsis='...'] - String to append when truncated
 * @returns {string} Truncated text with ellipsis if needed
 * @throws {TypeError} If length is not a positive number
 * 
 * @example
 * ```typescript
 * truncateText("This is a long text that needs truncation", 14)
 * // Returns "This is a long..."
 * 
 * truncateText("Short text", 100) // Returns "Short text"
 * truncateText("", 10) // Returns ""
 * ```
 */
export function truncateText(text: string, length = 100, ellipsis = '...'): string {
  if (!text) return ''
  
  if (typeof length !== 'number' || length <= 0) {
    throw new TypeError('Length must be a positive number')
  }
  
  if (text.length <= length) return text
  
  return text.slice(0, length) + ellipsis
}

/**
 * Convert a string to a URL-friendly slug.
 * 
 * @param {string} text - The text to convert to a slug
 * @param {string} [separator='-'] - Character to use as separator
 * @param {number} [maxLength=100] - Maximum length of the slug
 * @returns {string} URL-friendly slug
 * @throws {TypeError} If maxLength is not a positive number
 * 
 * @example
 * ```typescript
 * slugify("Hello World!") // Returns "hello-world"
 * slugify("Hello World!", "_") // Returns "hello_world"
 * slugify("Special @#$ Characters!") // Returns "special-characters"
 * slugify("") // Returns ""
 * ```
 */
export function slugify(text: string, separator = '-', maxLength = 100): string {
  if (!text) return ''
  
  if (typeof maxLength !== 'number' || maxLength <= 0) {
    throw new TypeError('maxLength must be a positive number')
  }
  
  // Escape the separator for regex use
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  
  let slug = text
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with separator
    .replace(/[\s\W-]+/g, separator)
    // Remove leading/trailing separators
    .replace(new RegExp(`^${escapedSeparator}+|${escapedSeparator}+$`, 'g'), '')
  
  // Truncate if necessary, ensuring we don't end with a separator
  if (maxLength && slug.length > maxLength) {
    slug = slug.slice(0, maxLength).replace(new RegExp(`${escapedSeparator}+$`), '')
  }
  
  return slug
}

// =============================================================================
// TEXT CAPITALIZATION FUNCTIONS
// =============================================================================

/**
 * Formats a word or phrase in title case, with special handling for minor words,
 * compound terms, acronyms, and special cases.
 *
 * @param {string} text - The text to format in title case
 * @param {TitleCaseOptions} [options] - Options for title case formatting
 * @returns {string} Text formatted in title case
 * @throws {TypeError} If the text parameter is null or undefined
 * 
 * @example
 * ```typescript
 * formatTitleCase("the quick brown fox") // Returns "The Quick Brown Fox"
 * formatTitleCase("api-driven development") // Returns "API-Driven Development"
 * formatTitleCase("e-commerce platform") // Returns "E-Commerce Platform"
 * formatTitleCase("iOS app development") // Returns "iOS App Development"
 * ```
 */
export function formatTitleCase(text: string, options?: TitleCaseOptions): string {
  if (text === null || text === undefined) {
    throw new TypeError('Text parameter cannot be null or undefined')
  }
  
  if (text === '') return ''
  
  const { 
    minorWords = DEFAULT_MINOR_WORDS, 
    preserveInternalCaps = true, 
    isFirstOrLastWord = false, 
    specialCases = {},
    hyphenatedWords = [],
    mergeSpecialCases = true,
    mergeHyphenatedWords = true
  } = options ?? {}
  
  // Merge special cases if option is enabled
  const effectiveSpecialCases: Record<string, string> = mergeSpecialCases 
    ? { ...DEFAULT_SPECIAL_CASES, ...specialCases }
    : { ...specialCases }
  
  // Merge hyphenated words if option is enabled
  const effectiveHyphenatedWords: string[] = mergeHyphenatedWords
    ? [...DEFAULT_HYPHENATED_WORDS, ...hyphenatedWords]
    : [...hyphenatedWords]
  
  // Check if the entire text matches a special case
  const specialCase = effectiveSpecialCases[text.toLowerCase()]
  if (specialCase) {
    return specialCase
  }
  
  // Handle hyphenated terms, slashes, and other delimiters
  if (/[-/()]/.test(text)) {
    // For terms like "professional-services", "Click-Through Rate (CTR)" or "A/B Testing"
    const parts = text.split(/(\s+|-|\(|\)|\/)/).filter(Boolean)
    
    // First check if the entire hyphenated term is in our list of proper hyphenated words
    const lowerText = text.toLowerCase()
    if (effectiveHyphenatedWords.includes(lowerText)) {
      // If it's a recognized hyphenated term, format each part while preserving the hyphen
      return lowerText.split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('-')
    }
    
    // Process each part and join them back together
    return parts.map((part, index) => {
      // Preserve parentheses (for acronyms) and slashes as-is
      if (part === '(' || part === ')' || part === '/') {
        return part
      }
      
      // For hyphens, determine whether to keep them or replace with spaces
      if (part === '-') {
        // Check if the surrounding parts form a recognized hyphenated term
        if (index > 0 && index < parts.length - 1) {
          const prevPart = parts[index - 1]
          const nextPart = parts[index + 1]
          
          if (prevPart && nextPart) {
            const potentialHyphenatedTerm = `${prevPart.toLowerCase()}-${nextPart.toLowerCase()}`
            
            // Check against our list of proper hyphenated words
            for (const hyphenatedWord of effectiveHyphenatedWords) {
              if (hyphenatedWord.includes(potentialHyphenatedTerm)) {
                return '-'
              }
            }
          }
        }
        
        // If not a recognized hyphenated term, replace with space
        return ' '
      }
      
      // Check if this part is a special case
      const partSpecialCase = effectiveSpecialCases[part.toLowerCase()]
      if (partSpecialCase) {
        return partSpecialCase
      }
      
      // Check if it's an acronym (all uppercase)
      if (part.toUpperCase() === part && part.length > 1 && /[A-Z]{2,}/.test(part)) {
        return part
      }
      
      // Otherwise format it as a regular word
      return formatTitleCase(part, {
        minorWords,
        preserveInternalCaps,
        isFirstOrLastWord: isFirstOrLastWord || index === 0 || index === parts.length - 1,
        specialCases: effectiveSpecialCases,
        hyphenatedWords: effectiveHyphenatedWords,
        mergeSpecialCases: false, // Already merged at the top level
        mergeHyphenatedWords: false // Already merged at the top level
      })
    }).join('')
  }
  
  // Split the text into words
  const words = text.split(' ')
  
  return words.map((word, index) => {
    // Check for special cases first
    const wordSpecialCase = effectiveSpecialCases[word.toLowerCase()]
    if (wordSpecialCase) {
      return wordSpecialCase
    }
    
    // Check for acronyms (all caps)
    if (word.toUpperCase() === word && word.length > 1 && /[A-Z]{2,}/.test(word)) {
      return word
    }
    
    // Check for internal capitalization (e.g., "iPhone")
    if (preserveInternalCaps && /[a-z][A-Z]/.test(word)) {
      return word
    }
    
    const lowerWord = word.toLowerCase()
    
    // Always capitalize first and last words, otherwise check if it's a minor word
    const isMinorWord = minorWords.includes(lowerWord)
    const shouldCapitalize = isFirstOrLastWord || index === 0 || index === words.length - 1 || !isMinorWord
    
    if (shouldCapitalize) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    } else {
      return lowerWord
    }
  }).join(' ')
}

/**
 * Formats text according to the specified capitalization strategy and options.
 *
 * @param {string} text - The text to format
 * @param {FormattingOptions} [options] - Configuration options for formatting
 * @returns {string} Properly formatted text
 * @throws {TypeError} If the text parameter is null or undefined
 * 
 * @example
 * ```typescript
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.TITLE_CASE })
 * // Returns "The Quick Brown Fox"
 * 
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.SENTENCE_CASE })
 * // Returns "The quick brown fox"
 * 
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.ALL_CAPS })
 * // Returns "THE QUICK BROWN FOX"
 * 
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.CAMEL_CASE })
 * // Returns "theQuickBrownFox"
 * 
 * formatText("api-driven development", { 
 *   capitalization: CapitalizationStrategy.TITLE_CASE,
 *   specialCases: { 'api': 'API' }
 * })
 * // Returns "API-Driven Development"
 * ```
 */
export function formatText(text: string, options?: FormattingOptions): string {
  if (text === null || text === undefined) {
    throw new TypeError('Text parameter cannot be null or undefined')
  }
  
  if (text === '') return ''
  
  const {
    capitalization = CapitalizationStrategy.TITLE_CASE,
    specialCases = DEFAULT_SPECIAL_CASES,
    hyphenatedWords = DEFAULT_HYPHENATED_WORDS,
    minorWords = DEFAULT_MINOR_WORDS,
    preserveInternalCaps = true,
    wordSeparator = ' ',
    wordDelimiters = DEFAULT_WORD_DELIMITERS,
    mergeSpecialCases = true,
    mergeHyphenatedWords = true,
    trimWhitespace = true,
    maxLength,
    truncateEllipsis = '...',
    removeExtraSpaces = true
  } = options ?? {}
  
  // Merge special cases if requested
  const effectiveSpecialCases: Record<string, string> = mergeSpecialCases 
    ? { ...DEFAULT_SPECIAL_CASES, ...specialCases }
    : { ...specialCases }
    
  // Convert hyphenated words array to Record for type safety and efficient lookups
  const hyphenatedWordsRecord: Record<string, string> = {}
  const defaultHyphenatedWordsArray = Array.isArray(DEFAULT_HYPHENATED_WORDS) 
    ? DEFAULT_HYPHENATED_WORDS 
    : []
  const customHyphenatedWordsArray = Array.isArray(hyphenatedWords) 
    ? hyphenatedWords 
    : []
    
  // Populate the record with the words we want to preserve
  const wordsToProcess = mergeHyphenatedWords 
    ? [...defaultHyphenatedWordsArray, ...customHyphenatedWordsArray]
    : [...customHyphenatedWordsArray]
    
  for (const word of wordsToProcess) {
    if (typeof word === 'string') {
      hyphenatedWordsRecord[word.toLowerCase()] = word
    }
  }
  
  // Check if the entire string matches a special case
  const lowerText = text.toLowerCase()
  const specialCase = effectiveSpecialCases[lowerText]
  if (specialCase !== undefined) {
    return postProcessText(specialCase, { trimWhitespace, maxLength, truncateEllipsis, removeExtraSpaces })
  }
  
  // Create a regex pattern from the word delimiters with proper escaping
  const escapedDelimiters = wordDelimiters.map(d => 
    d === '-' ? '\\-' : d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  )
  const delimiterPattern = new RegExp(`[${escapedDelimiters.join('')}]`)
  
  // Split the text into segments based on delimiters
  const segments = text.split(delimiterPattern)
  
  // Process each segment according to the capitalization strategy
  const formattedSegments = segments.map((segment, index, array) => {
    if (!segment) return segment // Handle empty segments
    
    // Only apply special case handling to whole words, not parts of words
    // This prevents false positives like capitalizing "api" in "rapidly"
    const wholeWordRegex = /^\w+$/
    const isWholeWord = wholeWordRegex.test(segment)
    const lowerSegment = segment.toLowerCase()
    
    // Check if segment matches a special case and is a whole word
    if (isWholeWord && lowerSegment in effectiveSpecialCases) {
      const result = effectiveSpecialCases[lowerSegment]
      if (result !== undefined) {
        return result
      }
    }
    
    // Check if segment is a hyphenated word that needs special handling
    if (segment.includes('-') && hyphenatedWordsRecord[lowerSegment] !== undefined) {
      return hyphenatedWordsRecord[lowerSegment]
    }
    
    // Apply the selected capitalization strategy
    switch (capitalization) {
      case CapitalizationStrategy.ALL_CAPS:
        return segment.toUpperCase()
        
      case CapitalizationStrategy.ALL_LOWERCASE:
        return segment.toLowerCase()
        
      case CapitalizationStrategy.SENTENCE_CASE:
        return index === 0 
          ? segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
          : segment.toLowerCase()
          
      case CapitalizationStrategy.CAMEL_CASE:
        return index === 0
          ? segment.toLowerCase()
          : segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
          
      case CapitalizationStrategy.PASCAL_CASE:
        return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
        
      case CapitalizationStrategy.SNAKE_CASE:
        return segment.toLowerCase()
        
      case CapitalizationStrategy.KEBAB_CASE:
        return segment.toLowerCase()
        
      case CapitalizationStrategy.CONSTANT_CASE:
        return segment.toUpperCase()
        
      case CapitalizationStrategy.DOT_CASE:
        return segment.toLowerCase()
        
      case CapitalizationStrategy.PRESERVE:
        return segment
        
      case CapitalizationStrategy.TITLE_CASE:
      default:
        return formatTitleCase(segment, {
          minorWords,
          preserveInternalCaps,
          isFirstOrLastWord: index === 0 || index === array.length - 1,
          specialCases: effectiveSpecialCases,
          hyphenatedWords: wordsToProcess, // Pass the array of hyphenated words
          mergeSpecialCases: false, // Already merged at the top level
          mergeHyphenatedWords: false // Already merged at the top level
        })
    }
  })
  
  // Determine the appropriate separator based on capitalization strategy
  let finalSeparator = wordSeparator
  if (capitalization === CapitalizationStrategy.CAMEL_CASE || 
      capitalization === CapitalizationStrategy.PASCAL_CASE) {
    finalSeparator = ''
  } else if (capitalization === CapitalizationStrategy.SNAKE_CASE ||
             capitalization === CapitalizationStrategy.CONSTANT_CASE) {
    finalSeparator = '_'
  } else if (capitalization === CapitalizationStrategy.KEBAB_CASE) {
    finalSeparator = '-'
  } else if (capitalization === CapitalizationStrategy.DOT_CASE) {
    finalSeparator = '.'
  }
  
  // Join the segments with the appropriate separator
  const result = formattedSegments.join(finalSeparator)
  
  // Apply post-processing
  return postProcessText(result, { trimWhitespace, maxLength, truncateEllipsis, removeExtraSpaces })
}
